import { checkWpsEnvIsNewPage } from './env';
import { isWpsEnv } from './get-user-info';

/**
 * 动态初始化脚本 common-2t-js performance-js
 *
 * @param route
 * @returns
 */
export const initScript = () => {
  return Promise.all([loadCommonJs2tAsync(), loadDwJsAsync()]);
};

// #region common-2t-js
let isLoadingCommonJs = false;
/** common-js是否加载完成 */
let isCommonJsLoaded = false;
const COMMON_JS_2T_SCRIPT = '//vas2t.wpscdn.cn/vas2t-prod/sdk/libs/js/common2t/20240403/common2t.pc.js';

const loadCommonJs2tStack = [] as Array<(value: unknown) => void>;
/**
 * 动态加载 commont 2t js, 解决适配新建应用页和服务集市中间页不同容器类型（服务集市中间页是小程序容器）
 *
 * @param route
 * @returns
 */
export const loadCommonJs2tAsync = () => {
  if (isCommonJsLoaded) {
    return Promise.resolve(true);
  }

  if (isLoadingCommonJs) {
    const p1 = new Promise((res) => {
      loadCommonJs2tStack.push(res);
    });
    return p1;
  }

  // 判断当前环境是否为小程序环境
  const isMiniProgram = !checkWpsEnvIsNewPage();
  if (isMiniProgram) {
    // 服务集市不插入 commonjs 2t
    removeScript(COMMON_JS_2T_SCRIPT);
    return Promise.resolve(false);
  }

  // 新建页场景下插入 commonjs 2t
  isLoadingCommonJs = true;
  return loadJsAsync(
    COMMON_JS_2T_SCRIPT,
    'async',
    'sha384-+wsQOMPbbuXsTugZYkXW6dYHXeIS+yJr4wrE4/Ix/IfDYZjgx1kJ8Jst7nAdgDRa'
  )
    .then(() => {
      isCommonJsLoaded = true;

      // 处理请求堆栈
      for (let index = 0; index < loadCommonJs2tStack.length; index++) {
        const resItem = loadCommonJs2tStack[index];
        resItem(true);
      }
      loadCommonJs2tStack.splice(0);

      emitLoadedCommonJs();
      return true;
    })
    .finally(() => {
      isLoadingCommonJs = false;
    });
};

/** common-js-2t 加载完成 */
export const EVENT_CODE_COMMON_JS_2T_LOADED = 'common-2t-js-loaded';

/**
 * 通知外部commonjs-2t加载完成
 */
export const emitLoadedCommonJs = () => {
  const msg = {
    code: EVENT_CODE_COMMON_JS_2T_LOADED,
  };

  window.postMessage(JSON.stringify(msg), location.origin);
};

export const loadedCommonJsHandler = (event: MessageEvent, cb: (value: unknown) => void) => {
  if (!event.data) {
    return;
  }
  try {
    const data = JSON.parse(event.data);
    if (data.code === EVENT_CODE_COMMON_JS_2T_LOADED) {
      cb(true);
    }
  } catch (err) {
    // console.error(err);
  }
};

export const bindLoadedCommonJsAsync = () => {
  return new Promise((res) => {
    if (isCommonJsLoaded) {
      res(true);
      return;
    }

    const handler = (event: MessageEvent) => {
      loadedCommonJsHandler(event, res);
    };

    window.addEventListener('message', handler);
  });
};
// #endregion

const removeScript = (url: string) => {
  const scriptEl = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);

  if (scriptEl) {
    document.removeChild(scriptEl);
  }
};

/**
 * 异步加载js, 加载成功返回 true
 *
 * @param url
 * @param type 加载js方式
 * @returns
 */
const loadJsAsync = (url: string, type = '', integrity?: string) => {
  const targetEl = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
  if (!targetEl) {
    // 其他场景下插入 commonjs 2t
    return insertScriptAsync(url, type, integrity);
  }

  return Promise.resolve(true);
};

/**
 * document 插入 2t commonJs
 *
 * @param type {string}: async defer
 * @returns
 */
const insertScriptAsync = (url: string, type = '', integrity?: string) => {
  return new Promise((res) => {
    const scriptEl = document.createElement('script');
    scriptEl.src = url;
    if (integrity) {
      scriptEl.setAttribute('integrity', integrity);
      scriptEl.setAttribute('crossorigin', 'anonymous');
    }

    // 处理脚本加载方式，同步还是异步
    if (type) {
      switch (type) {
        case 'async':
          scriptEl.async = true;
          break;
        case 'defer':
          scriptEl.defer = true;
          break;
        default:
          break;
      }
    }

    scriptEl.onload = () => {
      res('loaded');
    };
    document.head.appendChild(scriptEl);
  });
};

// #region 性能监控-js
/** 性能监控 */
const PERFORMANCE_JS_SCRIPT = '//vas2t.wpscdn.cn/vas2t-prod/sdk/libs/js/wps_perf/1.0.5/wps_perf_client_1.0.5.min.js';

let isLoadingPerformanceJs = false;

/**
 * 动态加载性能监测js
 *
 * @param route
 * @returns
 */
const loadPerformanceJsAsync = () => {
  if (isLoadingPerformanceJs) {
    return Promise.resolve();
  }

  const isWps = isWpsEnv();

  if (isWps) {
    const isMiniProgram = !checkWpsEnvIsNewPage();

    if (isMiniProgram) {
      // 服务集市不插入js
      removeScript(PERFORMANCE_JS_SCRIPT);
    } else {
      isLoadingPerformanceJs = true;
      // 其他场景下插入 commonjs 2t
      return loadJsAsync(PERFORMANCE_JS_SCRIPT)
        .then(() => {
          loadInlineScript(getPerformanceInitScript());
        })
        .finally(() => {
          isLoadingPerformanceJs = false;
        });
    }
  }

  return Promise.resolve();
};

/**
 * 低代码平台初始化脚本
 *
 * @returns
 */
const getPerformanceInitScript = () => {
  return `
  // 性能上报初始化
  window.WebVitalsWithClient &&
    new WebVitalsWithClient({
      elementtiming: 'lc-pf-id',
      bussiness: '2t-lowcode-platform',
      probability: 1,
      extendedFields: ['dom', 'parse_dom', 'redirect', 'appcache', 'lookup_domain', 'connect', 'request', 'request_document', 'response_document', 'ttfb', 'init_dom_tree', 'ready_start', 'c_cb_ts', 'c_cbf_ts', 'c_l_ts', 'c_lf_ts'],
      complete: function(perfResult) {
        window.perfResult = perfResult;
        console.log(perfResult);
      },
      fail: function(per_Err) {
        window.per_Err = per_Err;
        console.log(per_Err);
      }
    });
  `;
};
// #endregion

/** 加载行内脚本 */
const loadInlineScript = (content: string) => {
  const scriptEl = document.createElement('script') as HTMLScriptElement;
  scriptEl.type = 'text/javascript';
  scriptEl.innerHTML = content;

  document.head.appendChild(scriptEl);
};

// #region dw 数仓js
let isLoadingDwJs = false;
/** comonjs是否加载完成 */
let isDwJsLoaded = false;
const DW_JS_2T_SCRIPT = '//vas2t.wpscdn.cn/vas2t-prod/sdk/libs/js/dw/3.0.27/dw-web.min.js';
/**
 * 动态加载 commont 2t js, 解决适配新建应用页和服务集市中间页不同容器类型（服务集市中间页是小程序容器）
 *
 * @param route
 * @returns
 */
export const loadDwJsAsync = () => {
  if (isLoadingDwJs || isDwJsLoaded) {
    return Promise.resolve();
  }

  isLoadingDwJs = true;
  // 其他场景下插入 commonjs 2t
  return loadJsAsync(
    DW_JS_2T_SCRIPT,
    'async',
    'sha384-q/xu1+/VySQQVUBvx54+5fjAOGCXXQNTV4KLQ0py3VK4fvzCFFV6yQQPJRftXceH'
  )
    .then(() => {
      isDwJsLoaded = true;
      emitLoadedDwJs();
    })
    .finally(() => {
      isLoadingDwJs = false;
    });
};

/** common-js-2t 加载完成事件 */
export const EVENT_CODE_DW_JS_LOADED = 'dw-js-loaded';

/**
 * 通知外部dw-js加载完成
 */
const emitLoadedDwJs = () => {
  const msg = {
    code: EVENT_CODE_DW_JS_LOADED,
  };

  window.postMessage(JSON.stringify(msg), location.origin);
};

const dwLoadedCbs = [] as Array<() => void>;

/**
 * 数仓加载完成后的回调
 *
 * @param cb
 * @returns
 */
export const getDwLoaded = (cb: () => void) => {
  if (isDwJsLoaded) {
    cb();
    return;
  }

  if (!dwLoadedCbs.length) {
    // 事件监听绑定, 仅仅绑定一次
    window.addEventListener('message', (event) => {
      if (!event.data) {
        return;
      }
      try {
        const data = JSON.parse(event.data);

        if (data.code === EVENT_CODE_DW_JS_LOADED) {
          dwLoadedCbs.forEach((cb) => {
            try {
              cb();
            } catch (err) {
              // console.error(err);
            }
          });

          dwLoadedCbs.splice(0);
        }
      } catch (err) {
        // console.error(err);
      }
    });
  }

  // 存储dw回调事件
  dwLoadedCbs.push(cb);
};
// #endregion
